import { ErrorSeverity, RemiApp, RemiAppError } from "@remi/error";
import { remiLogger } from "@remi/logging";

const TRPC_PATH_PATTERN = /\/api\/trpc\/([^?]+)/;
// Vercel's edge rate limiter answers with an HTML 429 that React Query retries, so it must not page at HIGH.
const TOO_MANY_REQUESTS_STATUS = 429;

const extractTrpcPath = (input: RequestInfo | URL): string => {
  const url = input instanceof Request ? input.url : input.toString();
  return TRPC_PATH_PATTERN.exec(url)?.[1] ?? "unknown";
};

// A non-JSON tRPC body never came from the router: a failed one is a Vercel platform kill serving an
// HTML error page, a 200 one an intermediary (captive portal, carrier proxy) answering in its place —
// a missing requestId means the body never passed our edge at all.
export const guardTrpcResponse = (response: Response, input: RequestInfo | URL): void => {
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return;
  }
  // Unclassifiable without a content-type; tRPC's own parse failure is the better signal.
  if (response.ok && !contentType) {
    return;
  }

  const path = extractTrpcPath(input);
  const status = response.status;
  const error = new RemiAppError({
    app: RemiApp.REMI_NEXTJS,
    errorType: "TRPC_NON_JSON_RESPONSE",
    severity: status === TOO_MANY_REQUESTS_STATUS ? ErrorSeverity.LOW : ErrorSeverity.HIGH,
    fingerprintPrefix: `${path} · ${status}`,
    data: {
      path,
      status,
      contentType,
      vercelError: response.headers.get("x-vercel-error"),
      requestId: response.headers.get("x-vercel-id"),
    },
  });
  remiLogger.captureError(error);
  throw error;
};
